import { styled as muiStyled } from '@mui/material/styles'
import Layout from '@/layouts/index'
import Page from '@/components/Page'
import * as Yup from 'yup'
import {
  GlobalSettingWithWritersoftModules,
  Section,
  SeoMetadata,
  writerService,
} from '@/services/page'
import pathService from '@/services/path'
import type { GetStaticPaths, GetStaticProps } from 'next'
import { APPS } from '@/appConfig'
import type { ParsedUrlQuery } from 'querystring'
import styled from 'styled-components'
import { useForm, useWatch, UseFormReturn } from 'react-hook-form'
import Iconify from '@/components/Iconify'
import {
  useUserFetchActiveSubscriptionByAuth0IdQuery,
  useUserCreateStripeCheckoutSessionsMutation,
  FetchWriterGenericsQueryVariables,
  Format,
  useFetchBasicSetupConfigQuery,
  useFetchWriterGenericsQuery,
  ContextInputFragmentFragment,
  SupportingUseCaseFragmentFragment,
  SupportingToneFragmentFragment,
  SupportingLanguageFragmentFragment,
} from '@/generated/graphql'
import {
  Button,
  Card,
  CircularProgress,
  Container,
  DialogActions,
  DialogContent,
  Dialog,
  DialogContentText,
  DialogTitle,
  Stack,
  Typography,
  useTheme,
  TextField,
  Accordion,
  AccordionDetails,
  AccordionSummary,
} from '@mui/material'
import { TopLandingSection } from '@/sections/landing/Shares'
import { MotionViewport } from '@/components/animate'
import { FormProvider, RHFSelect, RHFTextField } from '@/components/hook-form'
import { LoadingButton } from '@mui/lab'
import { yupResolver } from '@hookform/resolvers/yup'
import _ from 'lodash'
import { useEffect, useState } from 'react'
import LoadingScreen from '@/components/LoadingScreen'
import moment from 'moment'
import CopyClipboard from '@/components/CopyClipboard'
import palette from '@/theme/palette'
import useAuth from '@/hooks/useAuth'
import layout from '@/theme/layout'
import { PATH_PAGE } from '@/routes/paths'

const defaultDialogValues = {
  toneId: '60572a639bdd4272b8fe358b',
  languageId: '60f853a39bb0df0013c6a588',
  useCaseId: '605838118c0a4a000c69c968',
}

const StyledContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  height: 100vh;
  width: 100%;
`

interface PathProps extends ParsedUrlQuery {
  appId: string
}

interface IndexProps {
  title?: string
  sections?: Section[]
  globalSettingWithWritersoftModules?: GlobalSettingWithWritersoftModules
  seoMetadataItemsCollection?: SeoMetadata[]
}

const StyledCard = styled(Card)`
  padding: 15px;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
`

const CardsContainer = styled.div`
  display: grid;
  grid-gap: 30px;
  grid-template-columns: repeat(4, 1fr);
  max-width: ${layout.maxWidth.desktop}px;
  margin: auto;
  @media (max-width: 768px) {
    max-width: 100%;
    grid-template-columns: repeat(1, 1fr);
  }
`

const OutputsContainer = styled.div`
  margin-top: 30px;
  display: grid;
  grid-gap: 30px;
  grid-template-columns: repeat(3, 1fr);
  width: 100%;
  margin: auto;
  @media (max-width: 768px) {
    max-width: 100%;
    grid-template-columns: repeat(1, 1fr);
  }
`

const RootStyle = muiStyled('div')(({ theme }) => ({
  paddingTop: theme.spacing(8),
  paddingBottom: theme.spacing(3),
  [theme.breakpoints.up('md')]: {
    paddingTop: theme.spacing(11),
    paddingBottom: theme.spacing(5),
  },
}))

const ContentStyle = muiStyled('div')(({ theme }) => ({
  overflow: 'hidden',
  position: 'relative',
}))

const Image = styled.img`
  width: 100%;
  border-radius: 10px;
`

WriterPage.getLayout = function getLayout(page: React.ReactElement) {
  return <Layout variant="main">{page}</Layout>
}

interface SectionProps {
  data: Awaited<ReturnType<typeof useFetchBasicSetupConfigQuery>['data']>
}

interface DialogComponentProps {
  fragment:
    | SupportingUseCaseFragmentFragment
    | SupportingToneFragmentFragment
    | SupportingLanguageFragmentFragment
}

function IsolateReset({ methods }: { methods: UseFormReturn<FormValuesProps> }) {
  const value = useWatch({
    control: methods.control,
    name: 'useCaseId',
    defaultValue: '',
  })

  useEffect(() => {
    const values = methods.getValues()
    methods.reset()
    Object.keys(values).forEach((key) => {
      if (Object.keys(defaultDialogValues).includes(key)) {
        const thisKey = key as keyof FormValuesProps
        methods.setValue(thisKey, values[thisKey])
      }
    })
  }, [methods, value])

  return <></>
}

const DialogComponent = (props: DialogComponentProps) => {
  const { user } = useAuth()
  const [
    userCreateStripeCheckoutSessionsMutation,
    { loading: createStripeCheckoutSessionsLoading },
  ] = useUserCreateStripeCheckoutSessionsMutation()
  const { data: userFetchActiveSubscriptionData, loading: userFetchActiveSubscriptionLoading } =
    useUserFetchActiveSubscriptionByAuth0IdQuery(
      user?.sub
        ? { variables: { auth0Id: user.sub } }
        : {
            skip: true,
          }
    )
  const [isOpen, setIsOpen] = useState(false)
  const [useCases, setUseCases] = useState<
    {
      id: string
      variables: FetchWriterGenericsQueryVariables
    }[]
  >([])

  const { data: fetchBasicSetupConfigQuery, loading: fetchBasicSetupConfigQueryLoading } =
    useFetchBasicSetupConfigQuery()

  const CommentSchema = Yup.object().shape({
    toneId: Yup.string().required('Tone is required'),
    languageId: Yup.string().required('Language is required'),
    useCaseId: Yup.string().required('Use Case is required'),
  })

  const defaultValues =
    props.fragment.__typename === 'SupportingUseCase'
      ? {
          useCaseId: props.fragment._id,
          languageId: defaultDialogValues.languageId,
          toneId: defaultDialogValues.toneId,
        }
      : props.fragment.__typename === 'SupportingLanguage'
      ? {
          useCaseId: defaultDialogValues.useCaseId,
          languageId: props.fragment._id,
          toneId: defaultDialogValues.toneId,
        }
      : props.fragment.__typename === 'SupportingTone'
      ? {
          useCaseId: defaultDialogValues.useCaseId,
          languageId: defaultDialogValues.languageId,
          toneId: props.fragment._id,
        }
      : {
          useCaseId: defaultDialogValues.useCaseId,
          languageId: defaultDialogValues.languageId,
          toneId: defaultDialogValues.toneId,
        }

  const methods = useForm<FormValuesProps>({
    resolver: yupResolver(CommentSchema),
    defaultValues,
  })

  const {
    handleSubmit,
    watch,
    formState: { isSubmitting },
  } = methods

  const handleClose = () => {
    setIsOpen(false)
    setUseCases([])
  }

  const isSubscribed = Boolean(userFetchActiveSubscriptionData?.subscriptions.length)
  const handleSubscribe = async () => {
    if (isSubscribed) return
    const { data } = await userCreateStripeCheckoutSessionsMutation()
    const redirectUrl = data?.createStripeCheckoutSessions?.redirectUrl ?? null
    if (redirectUrl) {
      window.location.assign(redirectUrl)
    }
  }
  if (fetchBasicSetupConfigQueryLoading || userFetchActiveSubscriptionLoading) return null

  return (
    <>
      <Button variant="outlined" fullWidth onClick={() => setIsOpen(true)}>
        Writersoft
      </Button>
      <Dialog fullScreen open={isOpen} onClose={handleClose}>
        <DialogTitle id="alert-dialog-title">AI Writer</DialogTitle>
        <DialogContent>
          <DialogContentText id="alert-dialog-description">
            AI Writer is a tool that lets you automatically generate high-quality content for
            various
          </DialogContentText>
          <br />
          <br />
          <FormProvider
            methods={methods}
            onSubmit={handleSubmit((data) => {
              const { languageId, toneId, useCaseId, ...rest } = data
              const language = _.find(fetchBasicSetupConfigQuery?.supportingLanguages ?? [], {
                _id: languageId,
              })
              const tone = _.find(fetchBasicSetupConfigQuery?.supportingTones ?? [], {
                _id: toneId,
              })
              const useCase = _.find(fetchBasicSetupConfigQuery?.supportingUseCases ?? [], {
                _id: useCaseId,
              })
              if (!language || !tone || !useCase) return Promise.resolve('')

              const restData = rest as { [key: string]: string }

              const contextInputs = Object.keys(restData)
                .map((keyLabel) => {
                  const input = (_.find(useCase.contextInputs, { keyLabel }) ??
                    {}) as ContextInputFragmentFragment
                  return {
                    ...input,
                    inputValue: restData[keyLabel] as string,
                  }
                })
                .map((contextInput) => {
                  const { __typename, ...rest } = contextInput
                  return rest
                })
                .map((contextInput) => ({
                  ...contextInput,
                  inputValue:
                    contextInput.isRequired && !contextInput.inputValue
                      ? 'Writersoft'
                      : contextInput.inputValue,
                }))

              setUseCases((useCases) => [
                {
                  id: moment().format('x'),
                  variables: {
                    inputs: [
                      {
                        id: moment().format('x'),
                        format: Format.Text,
                        language: {
                          _id: language._id,
                          slug: language.slug,
                        },
                        retryTimes: 3,
                        tone: {
                          _id: tone._id,
                          slug: tone.slug,
                        },
                        variations: 1,
                        useCase: {
                          _id: useCase._id,
                          slug: useCase.slug,
                          contextInputs,
                        },
                      },
                    ],
                  },
                },
                ...useCases,
              ])
            })}
          >
            <Stack spacing={3} alignItems="flex-end">
              <IsolateReset methods={methods} />
              <RHFSelect name="languageId" label="Language">
                <option disabled value="disabled">
                  Select one
                </option>
                {fetchBasicSetupConfigQuery?.supportingLanguages.map((supportingLanguage) => (
                  <option
                    key={supportingLanguage._id}
                    label={supportingLanguage.name}
                    value={supportingLanguage._id}
                  >
                    {supportingLanguage.name}
                  </option>
                ))}
              </RHFSelect>
              <RHFSelect name="toneId" label="Tone">
                <option disabled value="disabled">
                  Select one
                </option>
                {fetchBasicSetupConfigQuery?.supportingTones.map((supportingTone) => (
                  <option
                    key={supportingTone._id}
                    label={supportingTone.name}
                    value={supportingTone._id}
                  >
                    {supportingTone.name}
                  </option>
                ))}
              </RHFSelect>
              <RHFSelect name="useCaseId" label="Use Case">
                <option disabled value="disabled">
                  Select one
                </option>
                {fetchBasicSetupConfigQuery?.supportingUseCases.map((supportingUseCase) => (
                  <option
                    key={supportingUseCase._id}
                    label={supportingUseCase.name}
                    value={supportingUseCase._id}
                  >
                    {supportingUseCase.name}
                  </option>
                ))}
              </RHFSelect>
              {_.find(fetchBasicSetupConfigQuery?.supportingUseCases ?? [], {
                _id: watch('useCaseId'),
              })?.contextInputs.map((contextInput) => {
                switch (contextInput.inputType) {
                  case 'textarea':
                    return (
                      <RHFTextField
                        key={contextInput._id}
                        placeholder={contextInput.placeholder}
                        name={contextInput.keyLabel}
                        label={contextInput.label}
                        multiline
                        maxRows={3}
                      />
                    )
                  case 'text':
                    return (
                      <RHFTextField
                        key={contextInput._id}
                        name={contextInput.keyLabel}
                        placeholder={contextInput.placeholder}
                        label={contextInput.label}
                      />
                    )
                  default:
                    break
                }
              })}
              <LoadingButton
                disabled={!isSubscribed}
                fullWidth
                type="submit"
                variant="contained"
                loading={isSubmitting}
              >
                Generate
              </LoadingButton>
              {!isSubscribed && (
                <LoadingButton
                  variant="contained"
                  size="large"
                  onClick={handleSubscribe}
                  loading={createStripeCheckoutSessionsLoading}
                >
                  Subscribe Then Enjoy
                </LoadingButton>
              )}
            </Stack>
          </FormProvider>
          <br />
          <br />
          <br />
          <OutputsContainer>
            {useCases.map((useCase, useCaseIndex) => (
              <OutputComponent
                onRemove={() =>
                  setUseCases((useCases) => useCases.filter((_, index) => index !== useCaseIndex))
                }
                key={useCase.id}
                variables={useCase.variables}
                title={`#${useCaseIndex + 1} (${moment(Number(useCase.id)).format(
                  'YYYY-MM-DD HH:mm:ss'
                )})`}
              />
            ))}
          </OutputsContainer>
        </DialogContent>

        <DialogActions>
          <Button variant="outlined" onClick={handleClose}>
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </>
  )
}

const UseCasesSection = (props: SectionProps) => {
  const theme = useTheme()
  return (
    <RootStyle theme={theme}>
      <Container component={MotionViewport}>
        <TopLandingSection
          title={'Use Cases'}
          subtitle={
            'AI writer that lets you automatically generate high-quality content for various use cases.'
          }
        />
      </Container>
      <Container component={MotionViewport}>
        <CardsContainer>
          {props.data?.supportingUseCases.map((supportingUseCase) => (
            <StyledCard key={supportingUseCase._id}>
              <div>
                <Image alt={supportingUseCase.name} src={`/api/image/${supportingUseCase.image}`} />
                <Typography variant="subtitle1" sx={{ mt: 2, mb: 0.5 }}>
                  {supportingUseCase.name}
                </Typography>

                <Typography variant="body2" sx={{ mb: 2, color: 'text.secondary' }}>
                  {supportingUseCase.caption}
                </Typography>

                <Typography variant="body2" sx={{ mb: 2, color: 'text.secondary' }}>
                  {supportingUseCase.description}
                </Typography>
              </div>
              <DialogComponent fragment={supportingUseCase} />
            </StyledCard>
          ))}
        </CardsContainer>
      </Container>
    </RootStyle>
  )
}

const SupportingLanguagesSection = (props: SectionProps) => {
  const theme = useTheme()
  return (
    <RootStyle theme={theme}>
      <Container component={MotionViewport}>
        <TopLandingSection
          title={'Supporting Languages'}
          subtitle={
            'Supporting languages for AI writer that lets you automatically generate high-quality content for various use cases.'
          }
        />
        <CardsContainer>
          {props.data?.supportingLanguages.map((supportingLanguage) => (
            <StyledCard key={supportingLanguage._id}>
              <Typography variant="subtitle1">{supportingLanguage.name}</Typography>
            </StyledCard>
          ))}
        </CardsContainer>
      </Container>
    </RootStyle>
  )
}

const SupportingTonesSection = (props: SectionProps) => {
  const theme = useTheme()
  return (
    <RootStyle theme={theme}>
      <Container component={MotionViewport}>
        <TopLandingSection
          title={'Supporting Tones'}
          subtitle={
            'Supporting tones for AI writer that lets you automatically generate high-quality content for various use cases.'
          }
        />
        <CardsContainer>
          {props.data?.supportingTones.map((supportingTone) => (
            <StyledCard key={supportingTone._id}>
              <Typography variant="subtitle1">{supportingTone.name}</Typography>
            </StyledCard>
          ))}
        </CardsContainer>
      </Container>
    </RootStyle>
  )
}

const ButtonContainer = styled.div`
  display: grid;
  grid-gap: 30px;
  grid-template-columns: repeat(5, 1fr);
  @media (max-width: 768px) {
    grid-template-columns: repeat(3, 1fr);
  }
`

const OutputComponentStyledCard = styled(StyledCard)`
  &:hover {
    cursor: default;
  }
  background: ${palette.light.grey[200]} !important;
`

const InputsContainer = styled.div`
  display: grid;
  grid-gap: 15px;
  grid-template-columns: repeat(1, 1fr);
  @media (max-width: 768px) {
    grid-template-columns: repeat(1, 1fr);
  }
`

interface OutputComponentProps {
  variables: FetchWriterGenericsQueryVariables
  title: string
  onRemove: () => void
}

const OutputComponent = (props: OutputComponentProps) => {
  const { data: fetchWriterGenericsQueryData, loading: fetchWriterGenericsQueryLoading } =
    useFetchWriterGenericsQuery({
      variables: props.variables,
      fetchPolicy: 'no-cache',
    })

  const data = _.first(fetchWriterGenericsQueryData?.generics ?? [])

  const displayTexts: {
    label: string
    value: string
  }[] = [
    {
      label: 'Use Case',
      value: data?.useCase.name ?? '',
    },
    {
      label: 'Language',
      value: data?.language.name ?? '',
    },
    {
      label: 'Tone',
      value: data?.tone.name ?? '',
    },
  ]

  const customInput = !(props.variables.inputs instanceof Array)
    ? props.variables.inputs
    : _.first(props.variables.inputs)

  const customInputs = customInput?.useCase.contextInputs.map((contextInput) => ({
    label: contextInput.label,
    value: contextInput.inputValue ?? '',
  }))

  return (
    <OutputComponentStyledCard>
      <Typography variant="body1" style={{ marginBottom: 15 }}>
        {props.title}
      </Typography>
      {fetchWriterGenericsQueryLoading && <CircularProgress />}
      {!fetchWriterGenericsQueryLoading && (
        <>
          <Accordion>
            <AccordionSummary
              expandIcon={<Iconify icon={'eva:arrow-ios-downward-fill'} width={20} height={20} />}
            >
              <Typography variant="subtitle1">Writersoft Variations</Typography>
            </AccordionSummary>
            <AccordionDetails>
              <InputsContainer>
                {displayTexts.map((displayText) => (
                  <TextField
                    disabled
                    key={displayText.label}
                    label={displayText.label}
                    value={displayText.value}
                    fullWidth
                  />
                ))}
              </InputsContainer>
            </AccordionDetails>
          </Accordion>
          <Accordion defaultExpanded>
            <AccordionSummary
              expandIcon={<Iconify icon={'eva:arrow-ios-downward-fill'} width={20} height={20} />}
            >
              <Typography variant="subtitle1">Input Output</Typography>
            </AccordionSummary>
            <AccordionDetails>
              <InputsContainer>
                {(customInputs ?? []).map((customInput) => (
                  <CopyClipboard
                    key={customInput.label}
                    shareInputValue={customInput.value ?? ''}
                    inputProps={{ fullWidth: true }}
                  />
                ))}
                <CopyClipboard shareInputValue={data?.text ?? ''} />
              </InputsContainer>
            </AccordionDetails>
          </Accordion>
        </>
      )}

      {!fetchWriterGenericsQueryLoading && (
        <>
          <ButtonContainer>
            <Button variant="outlined" onClick={props.onRemove}>
              Remove
            </Button>
          </ButtonContainer>
        </>
      )}
    </OutputComponentStyledCard>
  )
}

type FormValuesProps = {
  toneId: string
  languageId: string
  useCaseId: string
}

export default function WriterPage({
  title,
  seoMetadataItemsCollection,
  globalSettingWithWritersoftModules,
}: IndexProps) {
  const { error, isLoading, user, login } = useAuth()

  const { data: fetchBasicSetupConfigQuery, loading: fetchBasicSetupConfigQueryLoading } =
    useFetchBasicSetupConfigQuery()

  const companyColor = globalSettingWithWritersoftModules?.companyColor ?? undefined

  if (isLoading) return <LoadingScreen companyColor={companyColor} />
  if (error)
    return (
      <StyledContainer>
        <div>{error.message}</div>
      </StyledContainer>
    )

  if (!user) {
    return (
      <StyledContainer>
        <Button variant="contained" size="large" onClick={() => login()}>
          點擊我登入
        </Button>
      </StyledContainer>
    )
  }

  const writersoftModules = globalSettingWithWritersoftModules?.writersoftModules ?? []
  fetchBasicSetupConfigQuery?.supportingUseCases
  const useCaseData = fetchBasicSetupConfigQuery
    ? {
        ...fetchBasicSetupConfigQuery,
        supportingUseCases:
          writersoftModules.length !== 0
            ? fetchBasicSetupConfigQuery.supportingUseCases.filter((supportingUseCase) =>
                writersoftModules.includes(supportingUseCase.name)
              )
            : fetchBasicSetupConfigQuery.supportingUseCases,
      }
    : undefined

  return (
    <Page title={title ?? ''} metadataItems={seoMetadataItemsCollection}>
      <RootStyle>
        <ContentStyle>
          {fetchBasicSetupConfigQueryLoading ? (
            <LoadingScreen companyColor={companyColor} />
          ) : (
            <>
              <UseCasesSection data={useCaseData} />
              <SupportingLanguagesSection data={fetchBasicSetupConfigQuery} />
              <SupportingTonesSection data={fetchBasicSetupConfigQuery} />
            </>
          )}
        </ContentStyle>
      </RootStyle>
    </Page>
  )
}

export const getStaticPaths: GetStaticPaths<PathProps> = async () => {
  const { getDefaultPaths } = pathService()
  const paths = APPS.flatMap((app) => getDefaultPaths(app.id))

  return {
    paths,
    fallback: true,
  }
}

export const getStaticProps: GetStaticProps<IndexProps, PathProps> = async ({ params, locale }) => {
  if (!params) throw new Error('No path parameters found')

  const { appId } = params

  const currentApp = APPS.find((app) => app.id === appId)
  const { getGlobalSettingWithWritersoftEnableModules } = writerService(appId)
  const [globalSettingWithModules] = await Promise.all([
    getGlobalSettingWithWritersoftEnableModules({ locale }),
  ])

  return {
    props: {
      title: globalSettingWithModules.seoMetadataItemsMap.title ?? currentApp?.title,
      globalSettingWithWritersoftModules: globalSettingWithModules,
    },
    revalidate: 3600,
  }
}
